import React, { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "./Sidebar";
import DashboardLoader from "./loader/DashboardLoader";

const PurchaseOrderForm = () => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSidebarItem, setActiveSidebarItem] = useState("purchase-order");

  const today = moment().format("YYYY-MM-DD");
  const lastDayOfMonth = moment().endOf("month").format("YYYY-MM-DD");

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      seller: "",
      buyer: "",
      orderDate: today,
      deliveryDueDate: lastDayOfMonth,
      items: [
        {
          title: "",
          mainVideoRate: 500,
          shortVideoRate: 500,
          shortVideosEligible: 0,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const handleSidebarItemClick = (itemId, path) => {
    navigate(path);
    setActiveSidebarItem(itemId);
  };

  const formValues = watch();
  const calculateItemTotal = (item) => {
    return (
      Number(item.mainVideoRate) +
      Number(item.shortVideoRate) * Number(item.shortVideosEligible)
    );
  };

  const calculateTotal = (items) => {
    return items.reduce((total, item) => total + calculateItemTotal(item), 0);
  };

  const onSubmit = async (data) => {
    console.log("Form submitted:", data);
    try {
      const formattedData = {
        sellerId: JSON.parse(data.seller).id,
        buyer: data.buyer,
        orderDate: data.orderDate,
        dueDate: data.deliveryDueDate,
        items: data.items.map((item) => ({
          videoId: JSON.parse(item?.title).id,
          shortVideosEligible: item?.shortVideosEligible,
          mainVideoRate: item?.mainVideoRate,

          shortVideoRate: item?.shortVideoRate,
        })),
        status: "PENDING",
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/create-purchase-order`,
        formattedData,
        {
          withCredentials: true,
        },
      );
    } catch (err) {
      console.log(err);
    }
  };

  const getAllSellers = async () => {
    try {
      const sellers = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-all-users`,
        {
          withCredentials: true,
        },
      );
      setSellers(sellers?.data?.users);
    } catch (err) {
      console.log(err);
    }
  };

  const getAllVideos = async () => {
    try {
      const videos = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-all-videos`,
        {
          path: "APPROVED",
          withCredentials: true,
        },
      );
      setVideos(videos.data);
    } catch (err) {
      console.log("Error while fetching videos");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([getAllSellers(), getAllVideos()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    } ;
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <DashboardLoader />
        </div>
      ) : (
        <div className="flex">
          <Sidebar
            activeSidebarItem={activeSidebarItem}
            onSidebarItemClick={handleSidebarItemClick}
          />
          <div className="flex-1">
            {!showPreview ? (
              <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="border-b pb-4">
                    <h1 className="text-2xl font-bold text-gray-800">
                      Purchase Order Form
                    </h1>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seller
                      </label>
                      <select
                        {...register("seller", {
                          required: "Please select a seller",
                        })}
                        className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select a seller</option>
                        {sellers && Array.isArray(sellers) && sellers.map((seller) => (
                          <option
                            key={seller._id}
                            value={JSON.stringify({
                              id: seller._id,
                              name: seller.username,
                            })}
                          >
                            {seller.username}
                          </option>
                        ))}
                      </select>
                      {errors?.seller && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors?.seller.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Buyer
                      </label>
                      <input
                        {...register("buyer", {
                          required: "Buyer is required",
                        })}
                        className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.buyer && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.buyer.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Order Date
                      </label>
                      <input
                        type="date"
                        {...register("orderDate")}
                        disabled
                        className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Delivery Due Date
                      </label>
                      <input
                        type="date"
                        {...register("deliveryDueDate")}
                        disabled
                        className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-50"
                      />
                    </div>
                  </div>
              

                  <div className="space-y-4 mt-6">
                    <div className="flex justify-between items-center">
                      <h2 className="text-xl font-semibold text-gray-800">
                        Items
                      </h2>
                      <button
                        type="button"
                        onClick={() =>
                          append({
                            title: "",
                            mainVideoRate: 500,
                            shortVideoRate: 500,
                            shortVideosEligible: 0,
                          })
                        }
                        className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors"
                      >
                        Add Item
                      </button>
                    </div>

                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="bg-gray-50 rounded-lg p-4 space-y-4"
                      >
                        <div className="flex justify-between items-center">
                          <h3 className="font-medium text-gray-700">
                            Item {index + 1}
                          </h3>
                          {fields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Item Description
                            </label>
                            <select
                              {...register(`items.${index}.title`, {
                                required: "Title is required",
                              })}
                              className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select a title</option>
                              {videos?.map((video) => {
    const publishPhase = video.phases.find((phase) => phase.name === "PUBLISH");
    const durationRange = video?.storyType?.durationRange;
    const formatTime = (timeRange) => {
      if (!timeRange) return null; 
      const firstNumber = parseInt(timeRange.split("-")[0], 10);
      return firstNumber === 0 ? 1 : firstNumber; 
    };
    console.log(durationRange);
    
    
    return (
      <option
        key={video._id}
        value={JSON.stringify({
          id: video._id,
          name: video.topic,
          phaseEndDate: publishPhase?.endDate || null,
          style: video?.videoStyle?.name,
          minLength: formatTime(durationRange)
        })}
      >
       {video.topic}  {video?.videoStyle?.name}
      </option>
      
    );
  })}
                            </select>
                            {errors.items?.[index]?.title && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.items[index].title.message}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Short Videos Eligible
                            </label>
                            <input
                              type="number"
                              {...register(
                                `items.${index}.shortVideosEligible`,
                                {
                                  required: "Required",
                                  min: {
                                    value: 0,
                                    message: "Cannot be negative",
                                  },
                                },
                              )}
                              className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {errors.items?.[index]?.shortVideosEligible && (
                              <p className="mt-1 text-sm text-red-600">
                                {
                                  errors.items[index].shortVideosEligible
                                    .message
                                }
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Main Video Rate (₹)
                            </label>
                            <input
                              type="number"
                              {...register(`items.${index}.mainVideoRate`)}
                              disabled
                              className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-50"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Short Video Rate (₹)
                            </label>
                            <input
                              type="number"
                              {...register(`items.${index}.shortVideoRate`)}
                              disabled
                              className="w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-50"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end space-x-4 pt-6">
                    <button
                      type="button"
                      onClick={() => setShowPreview(true)}
                      className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 transition-colors"
                    >
                      Preview
                    </button>
                    <button
                      type="submit"
                      className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between mb-6 border-b pb-4">
                  <h1 className="text-2xl font-bold text-gray-800">
                    Purchase Order Preview
                  </h1>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                  >
                    Edit
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-gray-700">Seller:</p>
                      <p className="mt-1">
                        {JSON.parse(formValues.seller).name}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">Buyer:</p>
                      <p className="mt-1">{formValues.buyer}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">Order Date:</p>
                      <p className="mt-1">
                        {moment(formValues.orderDate).format("LL")}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">
                        Delivery Due Date:
                      </p>
                      <p className="mt-1">
                        {moment(formValues.deliveryDueDate).format("LL") } by 5pm
                      </p>
                    </div>
                  </div>
                  <p>The company intends to acquire a diverse range of high-quality, engaging content from the aforementioned seller to enhance
                  its digital presence across YouTube, LinkedIn, and Instagram.</p>

                  <table className="w-full border-collapse mt-6">
                    <thead>
                      <p className="flex w-max">Items Ordered:</p>
                      <tr className="bg-gray-50">
                        <th className="border p-2 text-left font-medium text-gray-700">
                          Sr. No
                        </th>
                        <th className="border p-2 text-left font-medium text-gray-700">
                          Item Description/Title
                        </th>
                        <th className="border p-2 text-left font-medium text-gray-700">
                          Style
                        </th>
                        <th className="border p-2 text-left font-medium text-gray-700">
                          Expected Publishing Date
                        </th>
                        <th className="border p-2 text-left font-medium text-gray-700">
                          Min. length(in minutes)
                        </th>
                        
                        <th className="border p-2 text-right font-medium text-gray-700">
                          Main Video Rate (₹)
                        </th>
                        <th className="border p-2 text-right font-medium text-gray-700">
                          Short Video Rate (₹)
                        </th>
                        <th className="border p-2 text-right font-medium text-gray-700">
                          Short Videos Eligible
                        </th>
                        <th className="border p-2 text-right font-medium text-gray-700">
                          Total (₹)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {formValues.items.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border p-2">{index + 1}</td>
                          <td className="border p-2">
                            {JSON.parse(item.title).topic}
                          </td>
                          <td className="border p-2">
                            {JSON.parse(item.title).style}
                          </td>
                        
                            <td className="border p-2">
                            {moment(JSON.parse(item.title).phaseEndDate).format("DD.MM.YYYY")}


    </td>
    <td className="border p-2 items-center">
                            {JSON.parse(item.title).minLength}


    </td>
                          <td className="border p-2 text-right">
                            {item.mainVideoRate}
                          </td>
                          <td className="border p-2 text-right">
                            {item.shortVideoRate}
                          </td>
                          <td className="border p-2 text-right">
                            {item.shortVideosEligible}
                          </td>
                          <td className="border p-2 text-right">
                            {calculateItemTotal(item)}
                          </td>
                        </tr>
                      ))}
                      <tr className="font-bold bg-gray-50">
                        <td colSpan={8} className="border p-2 text-right">
                          Est. Total:
                        </td>
                        <td className="border p-2 text-right">
                          ₹{calculateTotal(formValues.items)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p>Total Cost of the Purchase: <u><b>₹{calculateTotal(formValues.items)}</b></u>, plus a performance-based bonus of the same amount.</p>

<p className="font-bold">Most Important Terms & Conditions:</p>
<ol>
    <li><b>1. Videos under 90 seconds:</b> Must be published on the company's Instagram, YouTube, and LinkedIn handles.</li>
    <li><b>2. Videos above 90 seconds but less than 10 minutes:</b> Must be published on the company's YouTube and LinkedIn handles only.</li>
    <li><b>3. Videos above 10 minutes:</b> Must be published only on the company's YouTube handle.</li>
    <li><b>4. Primary Performance Metric:</b> YouTube will serve as the primary platform for measuring the performance of a video.</li>
    <li>
        <b>5. Acceptance Criteria:</b>
        <ol type="a">
            <li><b>a. Videos under 90 seconds:</b> Eligible for payment only after achieving at least 400 views within the first 7 days.</li>
            <li><b>b. Videos above 90 seconds:</b> Eligible for payment only after achieving at least 200 views within the first 7 days.</li>
        </ol>
    </li>
    <li><b>6. Bonus Eligibility:</b> Videos exceeding the acceptance criteria will qualify for a bonus. For every additional 25% views beyond the acceptance threshold, the video will earn 25% of the bonus amount, up to a maximum bonus of 100%.</li>
    <li><b>7. Delivery Cutoff Date:</b> All deliverables must be submitted by the specified delivery cutoff date.</li>
    <li>
        <b>8. Non-Delivery & Penalty:</b> 
        Non-delivery includes missing the deadline, shifting delivery to the following month, or any delays beyond the cutoff date. For each undelivered or delayed item, a penalty of 50% of the payment for that item will be applied. This penalty will be deducted from the total earnings of the seller.
    </li>
    <li><b>9. Deductions:</b> A 2% TDS will be deducted from the final payment.</li>
    <li><b>10. Team Collaboration:</b> Content production is a collaborative effort. The consultant is responsible for task delegation, ensuring alignment, and coordinating with team members to meet delivery goals and prevent non-delivery.</li>
    <li>
        <b>11. Special Requirement for Podcast:</b> 
        The consultant must:
        <ul>
            <li>Organize, shortlist, and finalize a guest.</li>
            <li>Handle logistics, oversee recording, editing, and publishing.</li>
            <li>Coordinate across departments, ensuring proper planning, delegation, and execution.</li>
        </ul>
    </li>
</ol>

                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderForm;
